import { Fragment, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProgressProvider, useProgress } from '@/contexts/ProgressContext';
import type { Progress } from '@shared/schemas';
import { readLastScreen, writeLastScreen, type RestorableScreen } from '@/lib/lastScreen';
import { parseCourseHash, routeToHash, type CourseRoute } from '@/lib/courseRoute';
import { parseLegalHash } from '@/lib/legalRoute';
import { LegalScreen } from '@/components/legal/LegalScreen';
import { CookieConsent } from '@/components/CookieConsent';
import { ConsentGate } from '@/components/ConsentGate';
import { useAnalyticsConsent } from '@/hooks/useConsent';
import { GlossaryProvider } from '@/contexts/GlossaryContext';
import { Welcome } from '@/screens/Welcome';
import { Dashboard } from '@/screens/Dashboard';
import { Sprint } from '@/screens/Sprint';
import { TicketScreen } from '@/screens/Ticket';
// SBM merge (18 MAY 2026 · slot_sprint_board_merge): standalone Board import
// dropped · Board now exclusively consumed inline by Sprint.tsx via view-toggle
// (former /board standalone route retired per Genie OQ-7 pick).
import { useGlossaryShortcut } from '@/hooks/useGlossaryShortcut';
import { BadgeToast } from '@/components/BadgeToast';
import { SyncConflictToast } from '@/components/toasts';
import { useProgressSync } from '@/hooks/useProgressSync';
import { usePageAnalytics } from '@/hooks/usePageAnalytics';
import { useScreenTime } from '@/hooks/useScreenTime';
import { PomodoroWidget } from '@/components/PomodoroWidget';
import { ScreenLoader } from '@/components/ScreenLoader';
// slot_riso_cleanup G.5 (16 MAY 2026): AtelierFrame import dropped. 'atelier'
// screen route retired (Practice + Capstone retire orphaned the route entry).
// Atelier sub-app entry now only via NavShell sidebar Atelier item ->
// AtelierOverlay (ADR-010 overlay/modal pattern, z=40, sidebar-visible).
import { Onboarding } from '@/onboarding/Onboarding';
import { useAuth } from '@/hooks/useAuth';
import { useAuthContextOptional } from '@/contexts/AuthContext';
import { TopbarNavProvider } from '@/components/Topbar';
import { LoginScreen } from '@/screens/auth/Login';
import { ForgotPasswordScreen } from '@/screens/auth/ForgotPassword';
import { ResetPasswordScreen } from '@/screens/auth/ResetPassword';
import { VerifyEmailScreen } from '@/screens/auth/VerifyEmail';
import { OAuthCallbackScreen } from '@/screens/auth/OAuthCallback';
import { ConfirmLinkScreen } from '@/screens/auth/ConfirmLink';
import { migrateOldState } from '@/lib/migrate-old-state';
import { ChunkReloadBoundary, reloadOnceForStaleChunk } from '@/components/ChunkReloadBoundary';
import { entryUrlHadEmailLink } from '@/api/supabaseClient';
// ticketById import dropped G.0d: only consumer was activeTicket/ticketKind for
// FootNav `ticketKind` prop (retired).
// SPRINTS import dropped G.0d: only consumer was handleFootNav 'peek' case (FootNav retired).
import { useAchievements } from '@/hooks/useAchievements';
// resumeTicketId import dropped G.0d: only consumer was Morning render (retired).
import { activeSprint } from '@/lib/sprint-progression';
import { isSprintLockedForUser, roleOf } from '@/lib/access';
import { tierOf, nextPaidTierFor } from '@shared/access/roles';
// slot_riso_cleanup G.0d (16 MAY 2026) CF-A1-navshell-integration absorption:
//   Morning import dropped (Phase B Dashboard merger absorbed all features.
//     Dashboard.tsx docblock lines 35-43 enumerate. file deleted in G.0d).
//   FootNav + FootScreen imports dropped (each screen owns NavShell wrap per B/C/D/E/F.
//     App-level FootNav redundant. file deleted in G.0d).

// Code-split destination screens. Not visited every session.
// Eager: Welcome, Dashboard, Sprint, Ticket (daily flow).
// Practice + Capstone live inside the Atelier sub-app iframe per
// SANDBOX_ARCH 1.1 Variant B - not lazy-loaded into the main bundle.
const SprintPeek = lazy(() => import('@/screens/SprintPeek').then((m) => ({ default: m.SprintPeek })));
const SprintReview = lazy(() => import('@/screens/SprintReview').then((m) => ({ default: m.SprintReview })));
const FinalTest = lazy(() => import('@/screens/FinalTest').then((m) => ({ default: m.FinalTest })));
const MockExam = lazy(() => import('@/screens/MockExam').then((m) => ({ default: m.MockExam })));
const BlockExam = lazy(() => import('@/screens/BlockExam').then((m) => ({ default: m.BlockExam })));
const Profile = lazy(() => import('@/screens/Profile').then((m) => ({ default: m.Profile })));
// slot_role_management (10 JUL 2026): admin-only role/tariff manager.
const AdminRoles = lazy(() => import('@/screens/AdminRoles').then((m) => ({ default: m.AdminRoles })));
// slot_payments Phase C (11 JUL 2026): распаркованный ценовой экран (3 тарифа + upsell target).
const Pricing = lazy(() => import('@/screens/Pricing').then((m) => ({ default: m.Pricing })));
const MiniLesson = lazy(() => import('@/screens/MiniLesson').then((m) => ({ default: m.MiniLesson })));
// Phase D slot_riso_new_screens (16 MAY 2026): 2 NEW screens (Sprints/Karta + Progress).
const SprintsTimeline = lazy(() =>
  import('@/screens/SprintsTimeline').then((m) => ({ default: m.SprintsTimeline })),
);
const ProgressScreen = lazy(() =>
  import('@/screens/Progress').then((m) => ({ default: m.Progress })),
);
const Portfolio = lazy(() => import('@/screens/Portfolio').then((m) => ({ default: m.Portfolio })));
const PracticeSpace = lazy(() =>
  import('@/screens/PracticeSpace').then((m) => ({ default: m.PracticeSpace })),
);
const GlossaryOverlay = lazy(() =>
  import('@/components/GlossaryOverlay').then((m) => ({ default: m.GlossaryOverlay })),
);
const Flashcards = lazy(() => import('@/components/Flashcards').then((m) => ({ default: m.Flashcards })));

// H.4-EXT2.3 (17 MAY 2026 slot_riso_phase_h_final_smoke): TweaksPanel retired
// entirely per Genie post-implementation pivot. G.4 deliverable (320 LOC
// TweaksPanel.tsx + 12 test assertions) shipped 16 MAY · retired 17 MAY after
// Genie visual review concluded dev-only state switcher not needed (Riso
// production design self-explanatory · density/decoration/bg controls accessible
// via direct attribute editing during dev workflow if ever needed).
// NOT no_content_loss violation = explicit Genie retire pick. Lesson preserved
// in slot logs · component files stubbed (CF-G-mount-eperm-hard-delete pattern).

// slot_riso_cleanup G.0d (16 MAY 2026): 'morning' variant dropped (Dashboard merger).
// slot_riso_cleanup G.5 (16 MAY 2026): 'atelier' variant dropped (AtelierFrame
// retired, Atelier sub-app entry exclusively via NavShell overlay per ADR-010).
// SBM merge (18 MAY 2026 · slot_sprint_board_merge): 'board' standalone screen
// retired · view toggle inside Sprint.tsx handles board access. Sidebar NAV_ITEMS
// reshape: 'board' item removed; 'sprint' item renamed к Доска + 3-col SVG icon.
// Routing key 'sprint' preserved (single screen target for both list/board views).
type Screen =
  | 'welcome'
  | 'dashboard'
  | 'sprint'
  | 'ticket'
  | 'peek'
  | 'review'
  | 'finaltest'
  | 'mock'
  | 'exam'
  | 'profile'
  | 'minilesson'
  // Phase D slot_riso_new_screens (16 MAY 2026): NEW screens
  | 'sprints'
  | 'progress'
  // slot_homework_rework (11 JUN 2026): cross-course homework artefact showcase
  | 'portfolio'
  // slot_homework_rework Ф1 (16 JUN 2026): «Твоя практика» — пространство домашек-инструмент
  | 'practice'
  // slot_role_management (10 JUL 2026): admin-only «Управление ролями» panel.
  | 'admin'
  // slot_payments Phase C (11 JUL 2026): ценовой/тарифный экран (upsell target).
  | 'pricing';

export default function App() {
  useEffect(() => {
    const result = migrateOldState();
    if (result.migrated) {
      console.warn('[qa-studio] migrated legacy state:', result.details.join(', '));
    }
  }, []);

  // Stale-bundle recovery (8 JUN 2026): Vite fires `vite:preloadError` when a lazy
  // import()'s chunk fails to load — typically a tab left open across a prod deploy
  // whose chunk hashes have since changed (a learner hit a blank «перейти к тесту»
  // this way). Swallow the default unhandled rejection and hard-reload ONCE (guarded
  // against loops) to pull the fresh build. ChunkReloadBoundary is the render-time
  // backstop for the same class of failure.
  useEffect(() => {
    const onPreloadError = (e: Event) => {
      e.preventDefault();
      reloadOnceForStaleChunk();
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => window.removeEventListener('vite:preloadError', onPreloadError);
  }, []);

  return (
    <ThemeProvider>
      <LegalGate>
        <PublicPricingGate>
          <AuthGate>
            <ProgressProvider>
              <Router />
            </ProgressProvider>
            {/* First-login consent capture (invite-only · no signup form). Mounts
                only when authed; gates the app until the user accepts. */}
            <ConsentGate />
          </AuthGate>
        </PublicPricingGate>
      </LegalGate>
      {/* Cookie consent banner · always mounted (incl. login), hides itself on
          legal pages. Lives outside the gates so it shows on every surface. */}
      <CookieConsent />
    </ThemeProvider>
  );
}

/**
 * Legal-page gate (29 JUN 2026). Renders a standalone legal document
 * (#/terms · #/privacy · #/cookies · #/consent) ABOVE the auth gate, so the
 * pages open whether or not the visitor is signed in and are deep-linkable.
 * Otherwise renders the normal app subtree untouched. Subscribes to hash
 * changes so anchor clicks / back-forward swap the page in and out.
 */
function LegalGate({ children }: { children: ReactNode }) {
  const [hash, setHash] = useState<string>(
    typeof window !== 'undefined' ? window.location.hash : '',
  );
  useEffect(() => {
    const sync = () => setHash(window.location.hash);
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const docId = parseLegalHash(hash);
  if (docId) {
    return (
      <LegalScreen
        docId={docId}
        onBack={() => {
          if (typeof window === 'undefined') return;
          if (window.history.length > 1) window.history.back();
          else window.location.hash = '';
        }}
      />
    );
  }
  return <>{children}</>;
}

/**
 * Public pricing gate · slot_payments Phase C (11 JUL 2026 · Genie).
 *
 * The pricing screen must be reachable by ANONYMOUS visitors — the landing's paid
 * tariff CTAs deep-link to `/app/#/pricing` and the site is invite-only (no signup
 * form), so without this a prospect would hit the login wall instead of the
 * tariffs. Mirrors LegalGate: renders ABOVE AuthGate so `#/pricing` opens without
 * a session. Only intercepts for anon — a LOGGED-IN user falls through to the app
 * and gets the role-aware in-app pricing (owned / доплата=разница). No account /
 * entitlement is exposed here; the public view is role='free' (all «Купить» →
 * «скоро» fail-closed), so there's no security surface.
 */
function PublicPricingGate({ children }: { children: ReactNode }) {
  const [hash, setHash] = useState<string>(
    typeof window !== 'undefined' ? window.location.hash : '',
  );
  useEffect(() => {
    const sync = () => setHash(window.location.hash);
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const auth = useAuthContextOptional();
  const isPricingRoute = parseCourseHash(hash)?.screen === 'pricing';

  // Authed (or auth still resolving) → let the app render its role-aware pricing.
  if (!isPricingRoute || !auth || auth.isAuthed) return <>{children}</>;
  if (auth.isLoading) return <ScreenLoader label="Загрузка…" />;

  const backToApp = () => {
    if (typeof window !== 'undefined') window.location.hash = '';
  };
  return (
    <Suspense fallback={<ScreenLoader />}>
      <Pricing role="free" onLogo={backToApp} onDashboard={backToApp} />
    </Suspense>
  );
}

function pickInitialScreen(progress: Progress): Screen {
  if (!progress.onboarding.welcomeSeen) return 'welcome';
  // FS slot · restore last screen on hard reload. The FE-local `lastScreen`
  // tracker (lib/lastScreen.ts) is authoritative when set — covers ALL
  // restorable screens including `sprints` timeline / `progress` / `profile`
  // that aren't represented in the synced ProgressLast schema.
  const stored = readLastScreen();
  if (stored) return stored as Screen;
  // No FE-local last-screen tracker → either a brand-new user or a fresh login
  // after logout (logout purges the `qastudio.v1.*` namespace incl. last-screen).
  // In both cases land on «Сегодня» (dashboard) rather than deriving the last
  // ticket/sprint from synced progress — per Genie: login always opens Сегодня.
  // (Hard reload keeps the tracker, so it still restores the exact screen.)
  return 'dashboard';
}

function Router() {
  const { progress, setLast, setDemoMode, setTicketStatus } = useProgress();
  // slot_ux_polish UXP.2 (23 MAY 2026): pull auth into Router so
  // TopbarNavProvider can feed Topbar (user · logout) without per-screen prop-drilling.
  const auth = useAuth();
  // Mount the achievement engine so it auto-evaluates on every progress mutation.
  useAchievements();
  // FS slot · mount the progress sync glue so localStorage edits push to the
  // edge backend (debounced 200ms · per-branch LWW · 412 → refetch + retry).
  // `hydrated=false` while initial GET /progress is in flight — we render a
  // ScreenLoader instead of the screens to prevent the user from making edits
  // that would be silently clobbered by the arriving server snapshot.
  const { conflictKind, dismissConflict, retryConflict, hydrated } = useProgressSync();
  // Deep-link (28 JUN · open-in-new-tab): a course hash (#/ticket/MS-01) — from a
  // shared link or a middle/Ctrl-click opening a new tab — wins over the persisted
  // last-screen so the URL decides which page renders.
  const initialHashRoute =
    typeof window !== 'undefined' ? parseCourseHash(window.location.hash) : null;
  const initialScreen: Screen = initialHashRoute?.screen ?? pickInitialScreen(progress);
  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [sprintId, setSprintId] = useState<string>(
    initialHashRoute?.sprintId ?? (progress.last.sprintId || 'S01'),
  );
  const [peekSprintId, setPeekSprintId] = useState<string | null>(null);
  // Read-only DEMO (Phase 3): id of the sprint currently being peeked. Drives
  // ProgressContext.demoMode (all writes no-op) and lets Sprint bypass its lock
  // redirect. Stays set while navigating WITHIN the demo sprint (board ↔ its
  // tickets); cleared when leaving to dashboard / another sprint / review / test.
  const [demoSprintId, setDemoSprintId] = useState<string | null>(null);
  // «Купить полный курс» upsell modal (trial · Phase 4; real upgrade flow = Phase 5).
  const [upsellOpen, setUpsellOpen] = useState(false);
  // Какую веху открывает экран 'exam' (block1-exam после S05 · block2-exam после S09).
  const [examBank, setExamBank] = useState<string>(initialHashRoute?.examBank ?? 'block1-exam');
  useEffect(() => {
    setDemoMode(demoSprintId !== null);
  }, [demoSprintId, setDemoMode]);
  // Auto-exit demo when navigating out of the demo sprint's context. Board ↔ its
  // tickets keep demo; dashboard / another sprint / review / test / etc. clear it.
  useEffect(() => {
    if (demoSprintId === null) return;
    const inDemoContext =
      (screen === 'sprint' && sprintId === demoSprintId) || screen === 'ticket';
    if (!inDemoContext) setDemoSprintId(null);
  }, [screen, sprintId, demoSprintId]);
  const [ticketId, setTicketId] = useState<string>(
    initialHashRoute?.ticketId ?? (progress.last.ticketId || 'DOC-01'),
  );

  // Хаб «Твоя практика» · трекер экранного времени → achievements.activityByDay.
  // С открытым тикетом (доска · 11 JUL 2026) копит и per-ticket срез
  // activityByTicket — реальное время для сумм в колонках доски.
  useScreenTime(screen === 'ticket' ? ticketId : null);

  // Behaviour analytics · page views + time-on-page + navigation (8 JUN 2026).
  // `detail` carries the contextual id so per-ticket / per-sprint time is queryable.
  const analyticsDetail =
    screen === 'ticket'
      ? ticketId
      : screen === 'sprint'
        ? sprintId
        : screen === 'peek'
          ? peekSprintId
          : null;
  // Analytics is gated on the cookie consent opt-in (GDPR): we only bind the
  // user id — and therefore only track — when the visitor accepted analytics.
  // Revoking flips this to null, which unbinds + flushes in usePageAnalytics.
  const analyticsConsented = useAnalyticsConsent();
  usePageAnalytics(screen, analyticsDetail, analyticsConsented ? auth.user?.id ?? null : null);

  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [flashcardsOpen, setFlashcardsOpen] = useState(false);
  const [mockSlot, setMockSlot] = useState<'mock1' | 'mock2'>(initialHashRoute?.mockSlot ?? 'mock1');
  // slot_riso_cleanup G.5 (16 MAY 2026): atelierView + scenarioId state dropped.
  // sole consumer was AtelierFrame iframe (retired in G.5). Atelier sub-app owns
  // own internal routing per ADR-007 cross-app pattern. main app passes route hint
  // only via URL hash deep-link (#/atelier?...) consumed by NavShell parseAtelierHash.
  const [lessonId, setLessonId] = useState<string>(
    initialHashRoute?.lessonId ?? 'ml-detective-pro-patterns',
  );

  const welcomeSeen = progress.onboarding.welcomeSeen;
  const accentOverlay = progress.achievements.accentOverlay.selected;

  // Scroll preservation: when user navigates Sprint -> Ticket and back, restore the Sprint scroll
  // position instead of jumping to top. In-memory Map keyed by sprintId, cleared on tab close.
  const sprintScrollRef = useRef<Map<string, number>>(new Map());
  const screenRef = useRef(screen);
  const sprintIdRef = useRef(sprintId);
  useEffect(() => {
    screenRef.current = screen;
    sprintIdRef.current = sprintId;
  }, [screen, sprintId]);

  // slot_sync_storm_fix follow-up · the initial screen is picked at mount from
  // the PRE-hydration progress. After logout the progress cache is purged
  // (slot_logout_reset), so at the next login `welcomeSeen` reads false from the
  // empty cache and pickInitialScreen wrongly lands on Welcome — even though the
  // server says the user has already seen it. Re-pick ONCE, when initialFetch
  // has applied server truth (`hydrated`), before the user can navigate (the
  // ScreenLoader gate below blocks all interaction until `hydrated`). Idempotent
  // for the normal-reload path (cache intact → same screen).
  const progressRef = useRef(progress);
  progressRef.current = progress;
  const repickedRef = useRef(false);
  // Was the app opened on an explicit course deep-link? If so, the URL is
  // authoritative — do NOT let the post-hydrate re-pick bounce the user off the
  // deep-linked page back to dashboard/last (open-in-new-tab regression guard).
  const openedViaHashRef = useRef(initialHashRoute !== null);
  useEffect(() => {
    if (hydrated && !repickedRef.current) {
      repickedRef.current = true;
      if (openedViaHashRef.current) return; // deep-link wins
      const p = progressRef.current;
      // Re-sync the sprint/ticket selection from server-truth too — otherwise a
      // post-logout login (cache purged) leaves `ticketId` at its default
      // 'DOC-01' while pickInitialScreen lands on 'ticket' → "ticket not found".
      if (p.last.sprintId) setSprintId(p.last.sprintId);
      if (p.last.ticketId) setTicketId(p.last.ticketId);
      setScreen(pickInitialScreen(p));
    }
  }, [hydrated]);

  // Apply accent overlay to <html data-accent>
  useEffect(() => {
    const root = document.documentElement;
    if (accentOverlay === 'default') {
      root.removeAttribute('data-accent');
    } else {
      root.setAttribute('data-accent', accentOverlay);
    }
  }, [accentOverlay]);

  // H.4-EXT2.4 (17 MAY 2026 slot_riso_phase_h_final_smoke): default background
  // override к 'plain'. index.html still loads с data-bg="paper" по historical
  // default · this effect overrides on App mount per Genie pick «default plain».
  // TweaksPanel bg switcher retired (H.4-EXT2.3) · no runtime mutation expected
  // post-mount. Set unconditionally · matches expected Riso final visual state.
  useEffect(() => {
    document.documentElement.dataset.bg = 'plain';
  }, []);

  // FS slot · persist current screen to localStorage so hard reload restores
  // whichever view the user was on (not just sprint/ticket from synced
  // ProgressLast). Welcome is excluded — onboarding flow owns its first-mount
  // decision via progress.onboarding.welcomeSeen.
  useEffect(() => {
    if (screen === 'welcome') return;
    writeLastScreen(screen as RestorableScreen);
  }, [screen]);

  // Deep-link URL sync (28 JUN · open-in-new-tab). Mirror the current course
  // screen into window.location.hash so the address bar is shareable / openable
  // in a new tab, and so Back/Forward traverse course history. welcome/peek
  // aren't deep-linkable; the atelier owns its own #/atelier hash — don't clobber
  // it. First sync uses replaceState (no spurious history entry on mount); later
  // navigations pushState. The `!==` guard stops a popstate→setScreen→push loop.
  const firstHashSyncRef = useRef(true);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (screen === 'welcome' || screen === 'peek') return;
    if (window.location.hash.startsWith('#/atelier')) return;
    const hash = routeToHash({
      screen: screen as CourseRoute['screen'],
      sprintId,
      ticketId,
      lessonId,
      examBank,
      mockSlot,
    });
    if (window.location.hash !== hash) {
      if (firstHashSyncRef.current) window.history.replaceState(null, '', hash);
      else window.history.pushState(null, '', hash);
    }
    firstHashSyncRef.current = false;
  }, [screen, sprintId, ticketId, lessonId, examBank, mockSlot]);

  // Back/Forward (popstate) → re-derive the screen from the hash. New-tab landing
  // is handled by initialHashRoute; pushState/replaceState don't fire popstate, so
  // this runs only on genuine history nav. Non-course hashes (atelier/auth) ignored.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const r = parseCourseHash(window.location.hash);
      if (!r) return;
      if (r.sprintId) setSprintId(r.sprintId);
      if (r.ticketId) setTicketId(r.ticketId);
      if (r.lessonId) setLessonId(r.lessonId);
      if (r.examBank) setExamBank(r.examBank);
      if (r.mockSlot) setMockSlot(r.mockSlot);
      setScreen(r.screen);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const goLogo = useCallback(() => {
    if (!welcomeSeen) {
      setScreen('welcome');
    } else {
      setScreen('dashboard');
    }
    window.scrollTo({ top: 0 });
  }, [welcomeSeen]);

  const goDashboard = useCallback(() => {
    setScreen('dashboard');
    window.scrollTo({ top: 0 });
  }, []);

  const goSprint = useCallback(
    (id: string) => {
      setSprintId(id);
      setScreen('sprint');
      // FS slot · persist sprint navigation in progress.last so hard reload
      // restores the sprint screen (and clears any stale ticketId from a
      // previous ticket visit — otherwise pickInitialScreen sees the old
      // ticketId and bounces the user back to the ticket they already left).
      setLast(id);
      const savedY = sprintScrollRef.current.get(id);
      if (savedY && savedY > 0) {
        requestAnimationFrame(() => window.scrollTo({ top: savedY }));
      } else {
        window.scrollTo({ top: 0 });
      }
    },
    [setLast],
  );

  // SBM merge (18 MAY 2026): goBoard retired · NavShell 'board' nav key gone
  // (Sidebar NAV_ITEMS reshape) · Board access via Sprint.tsx view toggle.

  const goTicket = useCallback((id: string) => {
    if (screenRef.current === 'sprint' && sprintIdRef.current) {
      sprintScrollRef.current.set(sprintIdRef.current, window.scrollY);
    }
    setTicketId(id);
    setScreen('ticket');
    window.scrollTo({ top: 0 });
  }, []);

  // slot_riso_cleanup G.7 (17 MAY 2026): goWelcome callback retired.
  // Sole consumer was handleFootNav 'entry' case (FootNav retired G.0d).

  const goPeek = useCallback((id: string) => {
    setPeekSprintId(id);
    setScreen('peek');
    window.scrollTo({ top: 0 });
  }, []);

  // Open a sprint in read-only DEMO (Phase 3) — trial «заглянуть» into a locked
  // sprint. Sets demoSprintId (→ demoMode write-gate + Sprint lock bypass via the
  // `demo` prop). Does NOT setLast — demo navigation must not persist.
  const goSprintDemo = useCallback((id: string) => {
    setDemoSprintId(id);
    setSprintId(id);
    setScreen('sprint');
    window.scrollTo({ top: 0 });
  }, []);

  const goReview = useCallback((id?: string) => {
    if (id) setSprintId(id);
    setScreen('review');
    window.scrollTo({ top: 0 });
  }, []);

  const goFinalTest = useCallback((id?: string) => {
    if (id) setSprintId(id);
    setScreen('finaltest');
    window.scrollTo({ top: 0 });
  }, []);

  const goMock = useCallback((slot: 'mock1' | 'mock2') => {
    setMockSlot(slot);
    setScreen('mock');
    window.scrollTo({ top: 0 });
  }, []);

  // Экзамены-вехи: block1-exam (после S05) · block2-exam (после S09) · checkpoint_exams_spec.
  const goExam = useCallback((bankId: string = 'block1-exam') => {
    setExamBank(bankId);
    setScreen('exam');
    window.scrollTo({ top: 0 });
  }, []);

  // Pricing screen parked 1 MAY 2026 - re-wire when Phase 5 marketing kicks in.

  // slot_riso_cleanup G.2-G.3 (16 MAY 2026): goPractice + goCapstone callbacks
  // retired with retire of Practice.tsx + Capstone.tsx. Capstone release-claim
  // workflow migrated to Progress.tsx (D.3 migration, Q-1 E, verbatim
  // `no_content_loss` per Progress.tsx docblock line 54). Atelier sub-app
  // entry now via NavShell `atelier` nav key intercept -> AtelierOverlay
  // (ADR-010 overlay/modal pattern, z=40, sidebar-visible).

  const goProfile = useCallback(() => {
    setScreen('profile');
    window.scrollTo({ top: 0 });
  }, []);

  // slot_role_management (10 JUL 2026): admin-only role manager (Topbar avatar menu).
  const goAdmin = useCallback(() => {
    setScreen('admin');
    window.scrollTo({ top: 0 });
  }, []);

  // slot_payments Phase C (11 JUL 2026): ценовой/тарифный экран (upsell → checkout).
  const goPricing = useCallback(() => {
    setScreen('pricing');
    window.scrollTo({ top: 0 });
  }, []);

  const goMiniLesson = useCallback((id: string) => {
    setLessonId(id);
    setScreen('minilesson');
    window.scrollTo({ top: 0 });
  }, []);

  // Phase D slot_riso_new_screens: new screen navigators.
  const goSprints = useCallback(() => {
    setScreen('sprints');
    window.scrollTo({ top: 0 });
  }, []);

  const goProgress = useCallback(() => {
    setScreen('progress');
    window.scrollTo({ top: 0 });
  }, []);

  const goPortfolio = useCallback(() => {
    setScreen('portfolio');
    window.scrollTo({ top: 0 });
  }, []);

  // NB: name is goPracticeSpace (not goPractice) — `goPractice` is a retired G.2/G.3
  // callback guarded by App-routing.test; this is the NEW «Твоя практика» screen.
  const goPracticeSpace = useCallback(() => {
    setScreen('practice');
    window.scrollTo({ top: 0 });
  }, []);

  /**
   * NavShell nav dispatcher. Maps sidebar/bottom-tab keys to existing
   * App-level navigators. Pass-through to each screen's `onNav` prop.
   *
   * 'atelier' key is handled internally by NavShell (overlay toggle)
   * BUT we still receive the emit for URL/history bookkeeping (Phase G ready).
   * Unknown keys fall back to dashboard.
   */
  const dispatchScreenNav = useCallback(
    (key: string) => {
      switch (key) {
        case 'dashboard':
          goDashboard();
          break;
        case 'sprint': {
          const target = isSprintLockedForUser(progress, sprintId, roleOf(auth.user))
            ? activeSprint(progress).id
            : sprintId;
          goSprint(target);
          break;
        }
        case 'sprints':
          goSprints();
          break;
        case 'progress':
          goProgress();
          break;
        case 'portfolio':
          goPortfolio();
          break;
        case 'practice':
          goPracticeSpace();
          break;
        // SBM merge (18 MAY 2026): 'board' case retired · Sidebar NAV_ITEMS no
        // longer emits 'board' key ('sprint' item renamed к Доска · routing key
        // 'sprint' preserved).
        case 'buddy':
          // Phase D: no dedicated Buddy screen yet. graceful fallback to dashboard.
          goDashboard();
          break;
        case 'atelier':
          // NavShell owns overlay state. pass-through emits this key for
          // future URL/history sync. No screen swap needed.
          break;
        default:
          goDashboard();
          break;
      }
    },
    [goDashboard, goSprint, goSprints, goProgress, goPortfolio, goPracticeSpace, progress, sprintId, auth.user],
  );

  const openGlossary = useCallback(() => setGlossaryOpen(true), []);
  const closeGlossary = useCallback(() => setGlossaryOpen(false), []);
  const openFlashcards = useCallback(() => {
    setGlossaryOpen(false);
    setFlashcardsOpen(true);
  }, []);
  const closeFlashcards = useCallback(() => setFlashcardsOpen(false), []);

  // slot_vocabulary_navbar (17 MAY 2026 · VN.7 · Genie ack «a»): global Cmd/Ctrl+K
  // shortcut toggles GlossaryOverlay. Hook implementation в src/hooks/useGlossaryShortcut.ts
  // (extracted для isolated unit testing). Listener guards · HIDDEN_NAV_SCREENS,
  // typing-in-input ignore с drawer-search exception. See hook docblock для contract.
  useGlossaryShortcut({ screen, setOpen: setGlossaryOpen });

  // slot_riso_cleanup G.0d (16 MAY 2026): handleFootNav callback retired with
  // FootNav component. each screen owns its own NavShell wrap per B/C/D/E/F.
  // dispatchScreenNav (above) is the canonical navigation dispatcher now.
  // sidebar/bottom-tabs/atelier-overlay routing handled by NavShell internally.

  // slot_riso_cleanup G.0d (16 MAY 2026): activeTicket + ticketKind dropped.
  // only consumer was FootNav `ticketKind` prop (retired). Ticket lookup is now
  // owned by TicketScreen itself when navigating to ticket route.

  // slot_onboarding_redesign OB.3 (23 MAY 2026): Sprint 0 spotlight tour retired. <Onboarding/> below owns its own beat state + sequencing.

  // FS slot · block screens render until initialFetch /progress lands. Without
  // this gate the user can click Welcome → Start Day 1 in the ~200ms window
  // before the server snapshot arrives, and applyServerState silently
  // overwrites the optimistic local edit (looks like Welcome screen flashes).
  if (!hydrated) {
    return <ScreenLoader />;
  }

  return (
    <TopbarNavProvider value={{ goProfile, goAdmin, logout: auth.logout, user: auth.user }}>
    <GlossaryProvider open={openGlossary}>
      {screen === 'welcome' && (
        <Welcome
          onStart={() => goTicket('T00')}
          onSkip={() => {
            // slot_t00_ftue_rewrite (7 JUN 2026 · Genie): «я уже всё это знаю —
            // пропустить» auto-closes T00 — иначе карточка «Начни здесь» и сам
            // тикет висят на Дашборде у юзера, который явно сказал «всё знаю».
            setTicketStatus('T00', 'done');
            goDashboard();
          }}
        />
      )}
      {/* slot_riso_cleanup G.0d (16 MAY 2026): Morning render block dropped.
          Dashboard merger absorbed brief content (Phase B done). Returning
          users land on dashboard via pickInitialScreen. */}
      {screen === 'dashboard' && (
        <Dashboard
          onLogo={goLogo}
          onTicket={goTicket}
          onSprint={goSprint}
          onSprints={goSprints}
          onProgress={goProgress}
          /* SBM merge (18 MAY 2026): onBoard prop retired · Board access via
             Sprint.tsx view toggle (Dashboard nav «Доска» click routes к 'sprint'). */
          /* slot_riso_cleanup G.2-G.3 (16 MAY 2026): onPractice + onCapstone props dropped.
             Practice.tsx + Capstone.tsx retired. Dashboard already NO-OPed these per B.2 docblock.
             Capstone release-claim moved to Progress.tsx (Q-1 E D.3 verbatim migration). */
          onProfile={goProfile}
          onMiniLesson={goMiniLesson}
          onFinalTest={(id) => goFinalTest(id)}
          onNavFallback={dispatchScreenNav}
        />
      )}
      {screen === 'sprint' && (
        <Sprint
          sprintId={sprintId}
          demo={demoSprintId === sprintId}
          onLogo={goLogo}
          onTicket={goTicket}
          onDashboard={goDashboard}
          onFinalTest={(id) => goFinalTest(id)}
          onReview={(id) => goReview(id)}
          onOpenGlossary={openGlossary}
          onOpenFlashcards={openFlashcards}
          onStartMock={(slot) => goMock(slot)}
          onSprints={goSprints}
          onProgress={goProgress}
          onNavFallback={dispatchScreenNav}
        />
      )}
      {/* SBM merge (18 MAY 2026 · slot_sprint_board_merge):
          screen === 'board' standalone render block retired. Board access now
          exclusively inline в Sprint.tsx via view toggle (Genie OQ-7: retire
          полностью). dispatchScreenNav 'board' case + goBoard callback also
          retired. NavShell screen="board" wiring no longer instantiated. */}
      {screen === 'ticket' && (
        <TicketScreen
          ticketId={ticketId}
          onLogo={goLogo}
          onDashboard={goDashboard}
          onSprint={goSprint}
          onTicket={goTicket}
          onSprints={goSprints}
          onProgress={goProgress}
          onNavFallback={dispatchScreenNav}
        />
      )}
      {(screen === 'peek' ||
        screen === 'review' ||
        screen === 'finaltest' ||
        screen === 'mock' ||
        screen === 'exam' ||
        screen === 'profile' ||
        screen === 'minilesson' ||
        screen === 'sprints' ||
        screen === 'progress' ||
        screen === 'portfolio' ||
        screen === 'practice' ||
        screen === 'admin' ||
        screen === 'pricing') && (
        <ChunkReloadBoundary>
        <Suspense fallback={<ScreenLoader />}>
          {screen === 'peek' && peekSprintId && (
            <SprintPeek
              sprintId={peekSprintId}
              fromSprintId={sprintId}
              onLogo={goLogo}
              onClose={() => {
                setPeekSprintId(null);
                // «Вернуться» → Карта курса (peek is opened from the timeline), not
                // the sprint board · per Genie 2 JUN 2026.
                goSprints();
              }}
              onOpenInDemo={(id) => {
                setPeekSprintId(null);
                goSprintDemo(id);
              }}
            />
          )}
          {screen === 'review' && (
            <SprintReview
              sprintId={sprintId}
              onLogo={goLogo}
              onBackToSprint={() => goSprint(sprintId)}
              onSprints={goDashboard}
              onOpenNextSprint={(id) => goSprint(id)}
              onPeekFollowingSprint={(id) => goPeek(id)}
            />
          )}
          {screen === 'finaltest' && (
            <FinalTest
              sprintId={sprintId}
              onLogo={goLogo}
              onBackToSprint={() => goSprint(sprintId)}
              onPassToReview={() => goReview(sprintId)}
            />
          )}
          {screen === 'mock' && (
            <MockExam
              bankId={mockSlot === 'mock1' ? 'ctfl-mock-1' : 'ctfl-mock-2'}
              slot={mockSlot}
              onLogo={goLogo}
              onExit={() => goSprint('S06')}
              onComplete={() => goSprint('S06')}
            />
          )}
          {screen === 'exam' && (
            <BlockExam
              bankId={examBank}
              onLogo={goLogo}
              onExit={goSprints}
              onComplete={goSprints}
            />
          )}
          {screen === 'profile' && (
            <Profile onLogo={goLogo} onDashboard={goDashboard} onNavFallback={dispatchScreenNav} />
          )}
          {screen === 'minilesson' && (
            <MiniLesson lessonId={lessonId} onLogo={goLogo} onDashboard={goDashboard} onNavFallback={dispatchScreenNav} />
          )}
          {screen === 'sprints' && (
            <SprintsTimeline
              onLogo={goLogo}
              /* SBM.9 (18 MAY 2026): onSprint now accepts sprintId · fixes DEV-unlock
                 click-routing bug (was () => goSprint(activeSprint(progress).id)
                 which ignored clicked card · always opened active sprint regardless). */
              onSprint={(id) => goSprint(id)}
              onReview={(id) => goReview(id)}
              onPeek={(id) => goPeek(id)}
              onNav={dispatchScreenNav}
              onUpgrade={() => setUpsellOpen(true)}
              onExam={goExam}
            />
          )}
          {screen === 'progress' && (
            <ProgressScreen
              onLogo={goLogo}
              onSprint={(id) => goSprint(id ?? activeSprint(progress).id)}
              onSprints={goSprints}
              onNav={dispatchScreenNav}
            />
          )}
          {screen === 'portfolio' && (
            <Portfolio onLogo={goLogo} onNav={dispatchScreenNav} onOpenHomework={goTicket} />
          )}
          {screen === 'practice' && <PracticeSpace onLogo={goLogo} onNav={dispatchScreenNav} />}
          {screen === 'admin' && <AdminRoles onLogo={goLogo} onNav={dispatchScreenNav} />}
          {screen === 'pricing' && (
            <Pricing onLogo={goLogo} onDashboard={goDashboard} role={roleOf(auth.user)} />
          )}
        </Suspense>
        </ChunkReloadBoundary>
      )}
      {/* slot_riso_cleanup G.5 (16 MAY 2026): 'atelier' screen route + AtelierFrame
          render block retired. Atelier entry exclusively via NavShell sidebar
          Atelier item -> AtelierOverlay (ADR-010 overlay/modal pattern). Hash
          deep-link `#/atelier?route=...` opens overlay via NavShell parseAtelierHash. */}
      {/* slot_riso_cleanup G.0d (16 MAY 2026) CF-A1-navshell-integration absorption:
          App-level FootNav render block retired. each screen now owns its NavShell
          wrap per B/C/D/E/F (Sidebar + BottomTabs + AtelierOverlay primitives all
          internal to NavShell). NavShell intercepts 'atelier' nav key to toggle overlay. */}
      {/* slot_pomodoro_rework PR.5 (17 MAY 2026): PomodoroWidget mounted once
          at viewport-level · floating FAB bottom-right · single positioning
          paradigm для desktop + mobile (per Genie picks Q1+Q4). Widget owns
          own HIDDEN_NAV_SCREENS guard · renders null на login/welcome/finaltest/
          auth flows. Replaces old Topbar Pomodoro pill (retired в this PR).
          Z-index 42 above AtelierOverlay (z=40) per ADR-010. */}
      <PomodoroWidget screen={screen} />
      <Onboarding
        screen={screen}
        // Туториал живёт в первой сессии — активный спринт на этом этапе всегда S01.
        onOpenBoard={() => goSprint('S01')}
        onOpenMap={goSprints}
        onOpenGlossary={openGlossary}
      />
      {(glossaryOpen || flashcardsOpen) && (
        <Suspense fallback={null}>
          {glossaryOpen && (
            <GlossaryOverlay
              open={glossaryOpen}
              onClose={closeGlossary}
              onOpenFlashcards={openFlashcards}
            />
          )}
          {flashcardsOpen && <Flashcards open={flashcardsOpen} onClose={closeFlashcards} />}
        </Suspense>
      )}
      <BadgeToast />
      <SyncConflictToast
        kind={conflictKind}
        onDismiss={dismissConflict}
        onRetry={retryConflict}
      />
      {/* Upsell · slot_progression_plan_quiz Phase 4 · tier-aware copy (11 JUL 2026).
          slot_payments Phase C: CTA now routes to the pricing/checkout screen. */}
      {upsellOpen && (() => {
        const role = roleOf(auth.user);
        const current = tierOf(role);
        const next = nextPaidTierFor(role);
        const fmt = (n: number) => `S${String(n).padStart(2, '0')}`;
        const isTop = next?.role === 'pro';
        return (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Открыть больше спринтов"
          onClick={() => setUpsellOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 24,
            background: 'rgba(45, 27, 14, 0.45)',
          }}
        >
          <div
            className="state-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 460, textAlign: 'center', background: 'var(--paper, #FFFDF8)', padding: 28, borderRadius: 12 }}
          >
            <div style={{ fontSize: 34, marginBottom: 8 }}>🔓</div>
            <h2 style={{ margin: '0 0 10px', fontFamily: 'var(--ff-display)' }}>Открыть больше спринтов</h2>
            <p style={{ margin: '0 0 20px', color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              Твой тариф — «{current.label}»: открыты спринты до {fmt(current.maxOrd)}.
              {next && ` Перейди на «${next.label}» — откроются спринты до ${fmt(next.maxOrd)}${isTop ? ' (весь курс), плюс финальные тесты и Atelier' : ''}.`}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-primary"
                style={{ minHeight: 44 }}
                onClick={() => {
                  setUpsellOpen(false);
                  goPricing();
                }}
              >
                {next ? `Перейти к тарифам` : 'Смотреть тарифы'}
              </button>
              <button
                type="button"
                className="btn-ghost"
                style={{ minHeight: 44 }}
                onClick={() => setUpsellOpen(false)}
              >
                Не сейчас
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {/* H.4-EXT2.3 (17 MAY 2026): TweaksPanel mount block retired per Genie pivot.
          See docblock above TweaksPanelDev removal comment. */}
    </GlossaryProvider>
    </TopbarNavProvider>
  );
}

type AuthView = 'login' | 'forgot';

/**
 * Auth gate · SM.3c (slot_supabase_migration · scope-add, Genie option A).
 *
 * Renders the Supabase auth flow when there is no session, and the app once
 * signed in. The email-verify / password-reset / Google-OAuth landing paths
 * (`/verify`, `/reset-password`, `/auth/callback`) render their screen
 * regardless of session state — supabase-js (detectSessionInUrl) establishes
 * the session from the URL. Their handlers hard-navigate to `/` so the app
 * boots cleanly from the persisted session.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading, isAuthed } = useAuth();
  const [authView, setAuthView] = useState<AuthView>('login');

  // Special URL paths · session-agnostic landings (supabase-js
  // detectSessionInUrl handles session restore from the URL hash). After
  // each handler completes (success or failure) we hard-navigate к `/`
  // so the persisted session bootstraps the app cleanly.
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  // `replace` (not `href`): a consumed verify/reset URL must not linger as a history
  // entry — otherwise back/forward/reload resurrects the one-shot landing screen for
  // an already-logged-in user (8 JUN 2026 · learner stuck on «Ссылка не валидна»).
  const goHome = () => { if (typeof window !== 'undefined') window.location.replace('/app/'); };

  // Stale auth-landing URL hygiene (8 JUN 2026): internal navigation never changes the
  // URL (state router), so a consumed /reset-password or /verify URL can linger in
  // browser history. A logged-in user who lands back on it (reload / back / forward)
  // must drop into the app — NOT the dead one-shot-link wall. Guard on NO fresh
  // email-link markers (entryUrlHadEmailLink false); a genuine new link while logged in
  // still verifies normally through the branches below.
  const isStaleAuthLanding =
    !entryUrlHadEmailLink &&
    (path.startsWith('/reset-password') || path.startsWith('/verify'));
  if (isStaleAuthLanding) {
    if (isLoading) return <ScreenLoader label="Загружаю сессию…" />;
    if (isAuthed && user) {
      if (typeof window !== 'undefined') window.history.replaceState(null, '', '/');
      return <Fragment key={user.id}>{children}</Fragment>;
    }
    // not authed + no fresh markers → genuinely dead/expired link → fall through to the
    // existing invalid-link screen so the user can request a new one.
  }

  if (path.startsWith('/verify')) {
    return (
      <VerifyEmailScreen
        state="loading"
        onSuccess={goHome}
        onResend={goHome}
        onLogin={goHome}
      />
    );
  }
  if (path.startsWith('/reset-password')) {
    const token = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('token') ?? ''
      : '';
    return <ResetPasswordScreen token={token} onSuccess={goHome} onTokenInvalid={goHome} />;
  }
  if (path.startsWith('/auth/callback')) {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    return <OAuthCallbackScreen hash={hash} onSuccess={goHome} onFailure={goHome} />;
  }
  // Prefetch-safe email-link landing (invite / recovery / signup confirm / email
  // change). verifyOtp runs in JS here, so HTTP email scanners can't burn the
  // one-time token. See ConfirmLink.tsx · plan invite_link_prefetch_fix.
  if (path.startsWith('/auth/confirm')) {
    return <ConfirmLinkScreen onComplete={goHome} />;
  }

  if (isLoading) {
    return <ScreenLoader label="Загружаю сессию…" />;
  }
  if (!isAuthed || !user) {
    if (authView === 'forgot') {
      return <ForgotPasswordScreen onSwitchLogin={() => setAuthView('login')} />;
    }
    return (
      <LoginScreen
        onSuccess={() => setAuthView('login')}
        onForgotPassword={() => setAuthView('forgot')}
      />
    );
  }
  // Key the authed subtree by user id: a cross-tab account swap (shared Supabase
  // session) changes `user.id` while staying authed — remounting ProgressProvider
  // + Router gives the new identity a clean slate (fresh state + its own
  // initialFetch) instead of B inheriting A's open screen / in-memory progress.
  return <Fragment key={user.id}>{children}</Fragment>;
}
